import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchProfile } from '@zr/utilities/api/profile';
import { setExternalPreference } from '@zr/utilities/api/preference';

import { COMPONENT_MAP, JOB_HISTORY, SKILLS, UPLOAD_RESUME } from './suggestions';
import { suggestionReply } from './api';

// backend sends extra parameters as { reply_params: [{ a: b }, { c: d }] }
// however, we need to combine that all into one map as extra body parameters { a: b, c: d }
// supposedly, all key values will always be unique
const getSuggestionReplyParams = (suggestion) => {
  let allReplyParams = {};
  if (suggestion.reply_params) {
    suggestion.reply_params.forEach((replyParam) => {
      allReplyParams = {
        ...allReplyParams,
        ...replyParam,
      };
    });
  }
  return allReplyParams;
};

export default function usePlatformGeneratedScreeningQuestions({
  isInJs6650Test = false,
  numSuggestionsComplete,
  onDone,
  onError,
  onSuggestionAnswer,
  onSuggestionView,
  responseId,
  skillLimit,
  suggestions,
  totalScreeningQuestions,
  setIsLoading = () => {},
  encResponseId,
}) {
  const [profile, setProfile] = useState(null);
  const uploadedResumeRef = useRef(false);

  // fetch profile data
  // profile data is used for various actions, like adding a license and updating skills
  useEffect(() => {
    (async () => {
      try {
        const fetchedProfile = await fetchProfile();
        setProfile(fetchedProfile);
      } catch (e) {
        onError(e);
      }
    })();
  }, [onError]);

  // progress state that represents their wizard progress, combined into 1 object to reduce rerenders
  const [progress, setProgress] = useState({ stepIndex: 0, numAutoSkipped: 0 });
  const { stepIndex, numAutoSkipped } = progress;

  const numEligibleSuggestions = suggestions.length + numSuggestionsComplete;
  // display index/totalScreeningQuestions accounts for
  // previous employer screening questions
  // already answered questions from another session (totalScreeningQuestions does, but we need to calculate the index from scratch)
  // any auto skipped questions
  const displayIndex = (
    totalScreeningQuestions - suggestions.length - numAutoSkipped + stepIndex + 1
  );
  const displayTotalScreeningQuestions = totalScreeningQuestions - numAutoSkipped;
  // this will point to the current suggestion for several actions
  const suggestion = suggestions[stepIndex];

  const getSuggestionEventData = useCallback((index) => {
    if (isInJs6650Test) {
      return {
        pgsq_index: index + 1,
        pgsq_type: suggestions[index].suggestion_key,
        total_eligible_pgsq: numEligibleSuggestions,
        total_screening_questions: totalScreeningQuestions,
      };
    }

    return {
      question_number: numSuggestionsComplete + index + 1,
      question_type: suggestions[index].suggestion_key,
      // only count suggestions, not employer screening questions
      number_eligible: numEligibleSuggestions,
      response_id: responseId,
    };
  }, [
    isInJs6650Test, numEligibleSuggestions, numSuggestionsComplete, responseId, suggestions, totalScreeningQuestions,
  ]);

  const Question = COMPONENT_MAP[suggestion.suggestion_key];

  // this checks for any special cases that should auto skip the next step, when trying to advance() to that step
  async function handleNextSuggestionSpecialCases(nextProgress) {
    const { stepIndex: nextIndex, numAutoSkipped: nextNumAutoSkipped } = nextProgress;
    const nextSuggestion = suggestions[nextIndex];
    let autoSkipReason = null;
    if (nextSuggestion.suggestion_key === JOB_HISTORY && uploadedResumeRef.current) {
      // if uploaded resume, any change to job history will get overridden in parse approval flow
      autoSkipReason = 'uploaded_resume';
    } else if (nextSuggestion.suggestion_key === SKILLS && profile.skills?.length >= skillLimit) {
      // if they have >=30 skills somehow, don't bother with the skills step
      autoSkipReason = 'has_max_skills';
    }
    if (autoSkipReason) {
      try {
        await suggestionReply(encResponseId, nextSuggestion.suggestion_key, 'fulfilled', getSuggestionReplyParams(nextSuggestion));
      } catch (e) {
        // unsure what to do here since we're calling an api in the background, but I guess silently advance?
      }
      return {
        // increment the skip count, have advance() check for any conditions in the next step
        currentProgress: {
          stepIndex: nextIndex,
          numAutoSkipped: nextNumAutoSkipped + 1,
        },
        logAnswerData: {
          ...getSuggestionEventData(nextIndex),
          answer: 'skipped',
          auto_skipped: autoSkipReason,
        },
      };
    }
    return null;
  }

  // special note: we still need to await the last function call in the below functions
  // otherwise Formik will think we submitted earlier than expected

  async function advance({
    currentProgress,
    logAnswerData,
  }) {
    // default to progress in state, but as a new object to correctly rerender when react diffs the progress object
    // if currentProgress is passed in, we encountered some special case where we need to calculate the progress specially
    // then finally setProgress in the end
    const nextProgress = currentProgress || { ...progress };
    nextProgress.stepIndex += 1;
    const isComplete = nextProgress.stepIndex >= suggestions.length;
    onSuggestionAnswer({
      ...logAnswerData,
      complete: isComplete ? 1 : 0,
    });
    if (isComplete) {
      await onDone();
      // don't render the 'next' step if onDone is busy with something, otherwise we go out of bounds of suggestions
      return;
    }
    // if the next suggestion has a special case (auto skip), then keep calculating progress until we're done
    const specialProgress = await handleNextSuggestionSpecialCases(nextProgress);
    if (specialProgress) {
      // keep advancing if we handled special progression
      await advance(specialProgress);
    } else {
      // This wizard controls the various steps under the `PlatformSuggestion` screenType.
      // We need to `setIsLoading(false)` here because when one of the screens disables the close button
      // during its processing/loading phase, we need to re-enable the button here so that close
      // button is available again when we advance to the next step
      setIsLoading(false);

      // done calculating progress, so set react state now
      setProgress(nextProgress);
    }
  }

  async function handleSuggestionReply(answer) {
    try {
      await suggestionReply(encResponseId, suggestion.suggestion_key, answer, getSuggestionReplyParams(suggestion));
      await advance({
        logAnswerData: {
          ...getSuggestionEventData(stepIndex),
          answer: (answer === 'skipped') ? 'skipped' : 'answered',
        },
      });
    } catch (e) {
      onError(e);
    }
  }

  async function handleContinue(answer) {
    if (suggestion.suggestion_key === UPLOAD_RESUME) {
      // remember they accepted to replace their profile
      setExternalPreference('contact', 'replace_profile', 'yes');
      uploadedResumeRef.current = true;
    }

    await handleSuggestionReply(answer || 'fulfilled');
  }

  async function handleSkip() {
    await handleSuggestionReply('skipped');
  }

  // this ref and its logic is kind of ugly, but the effect logs the first view/possibly others multiple times
  // i believe this is due to getSuggestionEventData being updated later
  const hasCalledOnSuggestionViewIndex = useRef(new Set());

  useEffect(() => {
    if (!hasCalledOnSuggestionViewIndex.current.has(stepIndex)) {
      onSuggestionView(getSuggestionEventData(stepIndex));
      hasCalledOnSuggestionViewIndex.current.add(stepIndex);
    }
  }, [getSuggestionEventData, onSuggestionView, stepIndex]);

  return {
    displayIndex,
    displayTotalScreeningQuestions,
    handleContinue,
    handleSkip,
    numEligibleSuggestions,
    Question,
    profile,
    suggestion,
  };
}
